import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Scale, Globe, Briefcase, Building, User } from "lucide-react";
import MatrixBackground from "@/MatrixBackground";

const countries = [
  { code: "GB", name: "United Kingdom", jurisdiction: "English Law" },
  { code: "US", name: "United States", jurisdiction: "US Federal & State Law" },
  { code: "AF", name: "Afghanistan", jurisdiction: "Afghan Law" },
  { code: "AL", name: "Albania", jurisdiction: "Albanian Law" },
  { code: "DZ", name: "Algeria", jurisdiction: "Algerian Law" },
  { code: "AR", name: "Argentina", jurisdiction: "Argentine Law" },
  { code: "AU", name: "Australia", jurisdiction: "Australian Law" },
  { code: "AT", name: "Austria", jurisdiction: "Austrian Law" },
  { code: "BD", name: "Bangladesh", jurisdiction: "Bangladeshi Law" },
  { code: "BE", name: "Belgium", jurisdiction: "Belgian Law" },
  { code: "BR", name: "Brazil", jurisdiction: "Brazilian Law" },
  { code: "BG", name: "Bulgaria", jurisdiction: "Bulgarian Law" },
  { code: "CA", name: "Canada", jurisdiction: "Canadian Law" },
  { code: "CL", name: "Chile", jurisdiction: "Chilean Law" },
  { code: "CN", name: "China", jurisdiction: "Chinese Law" },
  { code: "CO", name: "Colombia", jurisdiction: "Colombian Law" },
  { code: "CR", name: "Costa Rica", jurisdiction: "Costa Rican Law" },
  { code: "HR", name: "Croatia", jurisdiction: "Croatian Law" },
  { code: "CY", name: "Cyprus", jurisdiction: "Cypriot Law" },
  { code: "CZ", name: "Czech Republic", jurisdiction: "Czech Law" },
  { code: "DK", name: "Denmark", jurisdiction: "Danish Law" },
  { code: "EG", name: "Egypt", jurisdiction: "Egyptian Law" },
  { code: "EE", name: "Estonia", jurisdiction: "Estonian Law" },
  { code: "FI", name: "Finland", jurisdiction: "Finnish Law" },
  { code: "FR", name: "France", jurisdiction: "French Law" },
  { code: "DE", name: "Germany", jurisdiction: "German Law" },
  { code: "GR", name: "Greece", jurisdiction: "Greek Law" },
  { code: "HK", name: "Hong Kong", jurisdiction: "Hong Kong Law" },
  { code: "HU", name: "Hungary", jurisdiction: "Hungarian Law" },
  { code: "IS", name: "Iceland", jurisdiction: "Icelandic Law" },
  { code: "IN", name: "India", jurisdiction: "Indian Law" },
  { code: "ID", name: "Indonesia", jurisdiction: "Indonesian Law" },
  { code: "IE", name: "Ireland", jurisdiction: "Irish Law" },
  { code: "IL", name: "Israel", jurisdiction: "Israeli Law" },
  { code: "IT", name: "Italy", jurisdiction: "Italian Law" },
  { code: "JP", name: "Japan", jurisdiction: "Japanese Law" },
  { code: "JO", name: "Jordan", jurisdiction: "Jordanian Law" },
  { code: "KE", name: "Kenya", jurisdiction: "Kenyan Law" },
  { code: "KW", name: "Kuwait", jurisdiction: "Kuwaiti Law" },
  { code: "LV", name: "Latvia", jurisdiction: "Latvian Law" },
  { code: "LB", name: "Lebanon", jurisdiction: "Lebanese Law" },
  { code: "LT", name: "Lithuania", jurisdiction: "Lithuanian Law" },
  { code: "LU", name: "Luxembourg", jurisdiction: "Luxembourg Law" },
  { code: "MY", name: "Malaysia", jurisdiction: "Malaysian Law" },
  { code: "MT", name: "Malta", jurisdiction: "Maltese Law" },
  { code: "MX", name: "Mexico", jurisdiction: "Mexican Law" },
  { code: "MA", name: "Morocco", jurisdiction: "Moroccan Law" },
  { code: "NL", name: "Netherlands", jurisdiction: "Dutch Law" },
  { code: "NZ", name: "New Zealand", jurisdiction: "New Zealand Law" },
  { code: "NG", name: "Nigeria", jurisdiction: "Nigerian Law" },
  { code: "NO", name: "Norway", jurisdiction: "Norwegian Law" },
  { code: "PK", name: "Pakistan", jurisdiction: "Pakistani Law" },
  { code: "PE", name: "Peru", jurisdiction: "Peruvian Law" },
  { code: "PH", name: "Philippines", jurisdiction: "Philippine Law" },
  { code: "PL", name: "Poland", jurisdiction: "Polish Law" },
  { code: "PT", name: "Portugal", jurisdiction: "Portuguese Law" },
  { code: "QA", name: "Qatar", jurisdiction: "Qatari Law" },
  { code: "RO", name: "Romania", jurisdiction: "Romanian Law" },
  { code: "RU", name: "Russia", jurisdiction: "Russian Law" },
  { code: "SA", name: "Saudi Arabia", jurisdiction: "Saudi Law" },
  { code: "SG", name: "Singapore", jurisdiction: "Singapore Law" },
  { code: "SK", name: "Slovakia", jurisdiction: "Slovak Law" },
  { code: "SI", name: "Slovenia", jurisdiction: "Slovenian Law" },
  { code: "ZA", name: "South Africa", jurisdiction: "South African Law" },
  { code: "KR", name: "South Korea", jurisdiction: "South Korean Law" },
  { code: "ES", name: "Spain", jurisdiction: "Spanish Law" },
  { code: "LK", name: "Sri Lanka", jurisdiction: "Sri Lankan Law" },
  { code: "SE", name: "Sweden", jurisdiction: "Swedish Law" },
  { code: "CH", name: "Switzerland", jurisdiction: "Swiss Law" },
  { code: "TW", name: "Taiwan", jurisdiction: "Taiwanese Law" },
  { code: "TH", name: "Thailand", jurisdiction: "Thai Law" },
  { code: "TR", name: "Turkey", jurisdiction: "Turkish Law" },
  { code: "UA", name: "Ukraine", jurisdiction: "Ukrainian Law" },
  { code: "AE", name: "United Arab Emirates", jurisdiction: "UAE Law" },
  { code: "UY", name: "Uruguay", jurisdiction: "Uruguayan Law" },
  { code: "VE", name: "Venezuela", jurisdiction: "Venezuelan Law" },
  { code: "VN", name: "Vietnam", jurisdiction: "Vietnamese Law" }
];

const sectors = [
  { id: "debt-recovery", name: "Debt Recovery" },
  { id: "corporate", name: "Corporate Law" },
  { id: "litigation", name: "Litigation" },
  { id: "real-estate", name: "Real Estate" },
  { id: "employment", name: "Employment Law" },
  { id: "intellectual-property", name: "Intellectual Property" },
  { id: "family", name: "Family Law" },
  { id: "general-practice", name: "General Practice" }
];

const Onboarding = ({ onComplete, onGoogleSignUp, loading }) => {
    // Set page title
  useEffect(() => {
    document.title = "Sign Up - Lawra AI";
    return () => {
      document.title = "Lawra AI - AI Legal Assistant for Lawyers & Law Firms | Save 70-80% Time";
    };
  }, []);

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    country: '',
    jurisdiction: '',
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptedTerms: false
  });

  const handleCountrySelect = (countryCode) => {
    const country = countries.find(c => c.code === countryCode);
    if (country) {
      setFormData({
        ...formData,
        country: country.name,
        jurisdiction: country.jurisdiction
      });
    }
  };

  const handleNext = () => {
    if (step === 1 && !formData.country) return;
    if (step === 2 && (!formData.name || !formData.email || !formData.password || !formData.confirmPassword || !formData.acceptedTerms)) return;
    
    if (step === 2 && formData.password !== formData.confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    
    if (step < 2) {
      setStep(step + 1);
    } else {
      // Call onComplete which will handle registration
      onComplete(formData);
    }
  };

  const handleGoogleSignUp = () => {
    // Check if country is selected (step 1 completed)
    if (!formData.country) {
      alert('Please select your country first');
      return;
    }
    // Call the Google sign-up handler with country/jurisdiction data
    onGoogleSignUp(formData.country, formData.jurisdiction);
  };

  return (
    <div className="onboarding-container">
      <MatrixBackground />
      <div className="onboarding-content">
        <div className="onboarding-logo">
          <Scale size={48} style={{ color: '#D4AF37', margin: '0 auto 1rem' }} />
          <h1 className="onboarding-title">Let's get you set up with Lawra AI</h1>
        </div>

        <div className="onboarding-progress-container">
          <div className="progress-bar-wrapper">
            <div className={`progress-bar-fill ${step === 2 ? 'full' : 'half'}`}>
              <div className="progress-bar-glow"></div>
            </div>
          </div>
          <div className="progress-labels">
            <span className={`progress-label ${step >= 1 ? 'active' : ''}`}>Country</span>
            <span className={`progress-label ${step >= 2 ? 'active' : ''}`}>Your Details</span>
          </div>
        </div>

        <Card className="onboarding-card">
          <CardHeader>
            <CardTitle className="card-title-gold">
              {step === 1 && "Select Your Country"}
              {step === 2 && "Your Details"}
            </CardTitle>
            <CardDescription>
              {step === 1 && "This helps Lawra understand your jurisdiction"}
              {step === 2 && "Help us personalise your experience"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 1 && (
              <div className="country-dropdown-container">
                <Label htmlFor="country">Country</Label>
                <Select onValueChange={handleCountrySelect} value={countries.find(c => c.name === formData.country)?.code || undefined}>
                  <SelectTrigger className="country-select-trigger">
                    <SelectValue placeholder="Select your country" />
                  </SelectTrigger>
                  <SelectContent className="country-select-content">
                    {countries.map((country) => (
                      <SelectItem key={country.code} value={country.code} className="country-select-item">
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.jurisdiction && (
                  <p className="jurisdiction-text">
                    Jurisdiction: <span className="gold-text">{formData.jurisdiction}</span>
                  </p>
                )}
              </div>
            )}

            {step === 2 && (
              <div className="details-form">
                <div className="form-group">
                  <Label htmlFor="name">Your Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="John Smith"
                    className="gold-input"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="john@example.com"
                    className="gold-input"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder="Min. 8 characters"
                    className="gold-input"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                    placeholder="Re-enter password"
                    className="gold-input"
                  />
                </div>
                
                {/* Divider */}
                <div style={{ margin: '1.5rem 0', textAlign: 'center' }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '1rem',
                    color: '#888'
                  }}>
                    <div style={{ flex: 1, height: '1px', background: '#333' }}></div>
                    <span>or</span>
                    <div style={{ flex: 1, height: '1px', background: '#333' }}></div>
                  </div>
                </div>

                {/* Google Sign-In Button */}
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGoogleSignUp}
                  disabled={loading}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    marginBottom: '1rem'
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 18 18">
                    <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                    <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
                    <path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z"/>
                    <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
                  </svg>
                  Continue with Google
                </Button>
              </div>
            )}

            {step === 2 && (
              <div className="terms-checkbox-container">
                <div className="terms-checkbox">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={formData.acceptedTerms}
                    onChange={(e) => setFormData({...formData, acceptedTerms: e.target.checked})}
                  />
                  <label htmlFor="terms">
                    I agree to the{' '}
                    <Link to="/terms" target="_blank" className="terms-link">
                      Terms and Conditions
                    </Link>
                    {', '}
                    <Link to="/privacy" target="_blank" className="terms-link">
                      Privacy Policy
                    </Link>
                    {', and '}
                    <Link to="/disclaimers" target="_blank" className="terms-link">
                      Disclaimers
                    </Link>
                    . I understand this platform does not provide legal advice.
                  </label>
                </div>
              </div>
            )}

            <div className="onboarding-actions">
              {step > 1 && (
                <Button
                  variant="outline"
                  onClick={() => setStep(step - 1)}
                  className="back-btn-onboarding"
                >
                  Back
                </Button>
              )}
              <Button
                onClick={handleNext}
                disabled={
                  loading ||
                  (step === 1 && !formData.country) ||
                  (step === 2 && (!formData.name || !formData.email || !formData.password || !formData.confirmPassword || !formData.acceptedTerms))
                }
                className="gold-button-primary"
              >
                {loading ? "Creating Account..." : (step === 2 ? "Create Account & Get 20 Credits" : "Continue")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Onboarding;
